// PanelSill.js
// Sill section for Steel Doors.
// Owns its controlMap; bubbles up the full section data via onChange(sectionData).
// All show/hide dependencies are driven by React state — no DOM manipulation.

import React, { useState, useEffect, useRef } from 'react';
import ExpandableSection from '../../../expandablesection';

import SillType   from '../../controls/sill/SillType';
import SillFinish from '../../controls/sill/SillFinish';
import SillSize   from '../../controls/sill/SillSize';
import SillHorn   from '../../controls/sill/SillHorn';

const ev = { value: '', label: '' };

const PanelSill = ({
  data         = {},
  onChange,
  disabled     = false,
  sectionId    = 'Sill',
  state,
  onExpandChange,
  forceExpanded,
}) => {

  // ── section state ─────────────────────────────────────────────────────────
  const [sillType,   setSillType]   = useState(data.sillType   || ev);
  const [sillFinish, setSillFinish] = useState(data.sillFinish || ev);
  const [sillSize,   setSillSize]   = useState(data.sillSize   || { value: '6 5/8"', label: '6 5/8"' });
  const [sillHorn,   setSillHorn]   = useState(data.sillHorn   || ev);

  // ── conditional visibility flags ─────────────────────────────────────────
  const [showSillChildren, setShowSillChildren] = useState(false); // SillFinish + SillSize
  const [showSillHorn,     setShowSillHorn]     = useState(false); // SillHorn (Standard only)

  // ── sync when parent loads saved data (once, when data arrives non-empty) ─
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    if (!data || Object.keys(data).length === 0) return;
    loadedRef.current = true;
    setSillType(data.sillType     || ev);
    setSillFinish(data.sillFinish || ev);
    setSillSize(data.sillSize     || { value: '6 5/8"', label: '6 5/8"' });
    setSillHorn(data.sillHorn     || ev);
  }, [data]);

  // ── bubble up — gated by isMounted ref so it never triggers its own re-run ─
  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    onChange?.({ sillType, sillFinish, sillSize, sillHorn });
  }, [sillType, sillFinish, sillSize, sillHorn]);

  // ── section title ─────────────────────────────────────────────────────────
  const title = sillType?.value
    ? `🪵 Sill  (${sillType.label})`
    : '🪵 Sill';

  // ── controlMap ────────────────────────────────────────────────────────────
  const controlMap = [
    {
      id: 'SillType',
      component: (
        <SillType
          value={sillType}
          onChange={setSillType}
          disabled={disabled}
          onSillChildrenVisible={(show, showHorn) => {
            setShowSillChildren(show);
            setShowSillHorn(showHorn);
          }}
        />
      ),
      position: [1, 1],
      status: 'active',
      expandableSectionId: sectionId,
    },
    // conditional: shown when SillType is selected
    ...(showSillChildren ? [
      {
        id: 'SillFinish',
        component: (
          <SillFinish
            value={sillFinish}
            onChange={setSillFinish}
            disabled={disabled}
            sillType={sillType}
          />
        ),
        position: [1, 2],
        status: 'active',
        expandableSectionId: sectionId,
      },
      {
        id: 'SillSize',
        component: (
          <SillSize
            value={sillSize}
            onChange={setSillSize}
            disabled={disabled}
          />
        ),
        position: [1, 3],
        status: 'active',
        expandableSectionId: sectionId,
      },
    ] : []),
    // conditional: shown only for Standard sill type
    ...(showSillHorn ? [{
      id: 'SillHorn',
      component: (
        <SillHorn
          value={sillHorn}
          onChange={setSillHorn}
          disabled={disabled}
        />
      ),
      position: [2, 1],
      status: 'active',
      expandableSectionId: sectionId,
    }] : []),
  ];

  return (
    <ExpandableSection
      title={title}
      sectionId={sectionId}
      state={state}
      onExpandChange={onExpandChange}
      forceExpanded={forceExpanded}
      panelLocation="Sill"
      controlMap={controlMap.filter(c => c.expandableSectionId === sectionId)}
    />
  );
};

export default PanelSill;
